import { usePageMeta } from "../seo";
import { CtaBand } from "../components/BookCta";
import { ParallaxImage, Reveal } from "../components/motion";
import { IMAGES, MEDIA } from "../data/site";

const CHAPTERS = [
    {
        n: "01",
        title: "It started with a blanket in McKinney",
        text: "Enchanting Escapes began with a simple belief: the most beautiful celebrations don't need a ballroom. They need soft light, comfortable floor cushions, a table set with intention — and the people you love.",
    },
    {
        n: "02",
        title: "Event stylists first, never a kit",
        text: "What started as styling intimate date nights for friends grew into one of DFW's most personal luxury styling studios. Every setup is designed in-house — handcrafted low tables, custom color stories, refined tablescapes. Never a cookie-cutter kit dropped in a park.",
    },
    {
        n: "03",
        title: "Effortless is the whole point",
        text: "We scout the location, arrive early, style everything and return for breakdown. Cliffside proposals at golden hour, blush-toned bridal showers in Frisco gardens, milestone birthdays under the Plano sky — you never lift a finger. Or a cushion.",
    },
];

export default function About() {
    usePageMeta(
        "About | Enchanting Escapes — Luxury Event Styling in McKinney, TX",
        "Meet Ashley and the story behind Enchanting Escapes — McKinney's personal luxury picnic and outdoor event styling studio serving Dallas-Fort Worth."
    );

    return (
        <main data-testid="about-page">
            <section className="py-20 sm:py-28">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <Reveal>
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            Our Story
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D230A] leading-tight">
                            Rooted in McKinney, made for meaningful moments
                        </h1>
                        <p className="mt-7 text-[#6E5B3D] leading-relaxed text-base sm:text-lg">
                            From our first styled picnic to hundreds of escapes across the
                            Dallas-Fort Worth area, one thing has never changed: no chairs,
                            no clutter — just warm, refined, unforgettable scenes designed
                            around your story.
                        </p>
                        <p className="mt-8 font-display italic text-2xl text-[#C18E8B]">
                            — Ashley & The Enchanting Escapes Family, McKinney TX
                        </p>
                    </Reveal>
                    <div className="relative" data-testid="about-image">
                        <Reveal delay={0.15}>
                            <ParallaxImage
                                src={MEDIA.fountain}
                                alt="Real Enchanting Escapes luxury picnic setup beside a fountain with LED candles in McKinney Texas"
                                className="arch-frame aspect-[4/5] shadow-xl"
                                amount={9}
                            />
                        </Reveal>
                        <Reveal delay={0.35} className="absolute -bottom-8 -left-4 sm:-left-8 w-40 sm:w-52">
                            <img
                                src={MEDIA.tent}
                                alt="Real Enchanting Escapes canopy setup with roses, floor cushions and LED candles"
                                loading="lazy"
                                className="rounded-2xl border-4 border-[#FAF7F2] shadow-lg w-full aspect-[3/4] object-cover"
                            />
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#FFD2CE]/40 py-24 sm:py-36" data-testid="about-difference">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-16 sm:mb-24">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            The Difference
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                            Not your standard picnic company — in three chapters
                        </h2>
                    </Reveal>
                    <div className="space-y-20 sm:space-y-28">
                        {CHAPTERS.map((c, i) => (
                            <div
                                key={c.n}
                                data-testid={`about-chapter-${c.n}`}
                                className={`grid md:grid-cols-12 gap-6 md:gap-10 items-start ${i % 2 === 1 ? "md:text-right" : ""}`}
                            >
                                <Reveal className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                    <span className="chapter-number">{c.n}</span>
                                </Reveal>
                                <Reveal delay={0.15} className={`md:col-span-9 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                                    <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#2D230A] leading-tight">
                                        {c.title}
                                    </h3>
                                    <p className={`mt-4 text-[#6E5B3D] leading-relaxed max-w-xl ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                                        {c.text}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand
                heading="Let's write your story together"
                subtext="Whether it's a proposal in McKinney or a birthday in Dallas, we'd be honored to style it."
            />
        </main>
    );
}
